import { ReactElement, use } from 'react';
import { Controller, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { v4 as uuidv4 } from 'uuid';
import BreadCrumb from 'shared/components/bread-crumb/bread-crumb/bread-crumb';
import SelectField from 'shared/components/formFields/select-field';
import defaultValues from 'shared/constants/defaultValues';

import ReadDevicesResponse from 'features/wandering-boundary-settings/domain/models/read-devices-response';
import ReadDomipDevicesResponse from 'features/wandering-boundary-settings/domain/models/read-domip-devices-response';
import WanderingBoundaryPassage from 'features/wandering-boundary-settings/domain/models/wandering-boundary-passage';
import WanderingBoundaryPassageRowProviderContext from 'features/wandering-boundary-settings/providers/contexts/wandering-boundary-passage-row-provider-context';
import WanderingBoundarySettingsProviderContext from 'features/wandering-boundary-settings/providers/contexts/wandering-boundary-provider-context';
import styles from './domip-select.module.scss';

interface Props {
  fieldName: Path<WanderingBoundaryPassage>;
}

const DomipSelect = ({ fieldName }: Readonly<Props>): ReactElement => {
  const { t } = useTranslation('wanderingBoundarySettings');
  const { form } = use(WanderingBoundaryPassageRowProviderContext);
  const {
    detailsState: { domipDevices },
  } = use(WanderingBoundarySettingsProviderContext);

  const noDomipDevices = !domipDevices || domipDevices.length === 0;

  const selectMenuItem = (device: ReadDevicesResponse | ReadDomipDevicesResponse): ReactElement => (
    <MenuItem key={device.id} value={device.id} sx={{ borderRadius: '24px', alignItems: 'flex-start' }} className={styles.menuItem}>
      <Typography variant="subtitle1">{device.name}</Typography>
      <BreadCrumb showEllipsis={device.breadCrumbs.length > 2} allElementNames={device.breadCrumbs}>
        {device.breadCrumbs.map((name) => (
          <Typography sx={{ margin: '2px 0px' }} key={name} variant="caption">
            {name}
          </Typography>
        ))}
      </BreadCrumb>
    </MenuItem>
  );

  const defaultSelectMenuItem = (): ReactElement => (
    <MenuItem key={uuidv4()} value={defaultValues.defaultSelectValue} sx={{ borderRadius: '24px' }}>
      <Typography variant="subtitle1">{defaultValues.defaultSelectValue}</Typography>
    </MenuItem>
  );

  return (
    <Controller
      name={fieldName}
      control={form.control}
      render={({ field, fieldState: { error } }) => {
        if (noDomipDevices) {
          return (
            <TextField
              label={t('wanderingBoundaryPassageFields.domipSelectLabel')}
              variant="outlined"
              value={t('wanderingBoundaryPassageFields.noDomipDevicesLabel')}
              disabled
              className={styles.noDevicesField}
              error={!!error}
              helperText={error?.message?.toString()}
              slotProps={{
                input: {
                  sx: { fontStyle: 'italic' },
                },
              }}
            />
          );
        }

        return (
          <SelectField
            {...field}
            error={!!error}
            helperText={error?.message?.toString()}
            variant="outlined"
            label={t('wanderingBoundaryPassageFields.domipSelectLabel')}
            value={field.value || ''}
            onChange={(event) => field.onChange(event.target.value || null)}
            className={styles.selectField}
          >
            {[defaultSelectMenuItem(), ...domipDevices.map(selectMenuItem)]}
          </SelectField>
        );
      }}
    />
  );
};

export default DomipSelect;
